import { useState, useRef, useEffect } from 'react';
import { getToken } from 'firebase/app-check';
import { appCheck } from './useFirebase';

export const OLLAMA_MISSING_CREDENTIALS_ERROR = 'API kulcs nincs beállítva, kérlek a Builty AI használatához előbb add meg az ollama API kulcsod a Beállítások menüpontban!';

interface UseOllamaOptions {
  onChunk: (chunk: string) => void;
  onDone: (fullText: string) => void;
  onError: (error: string) => void;
}

export function useOllama() {
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const getLocalStorageValue = (key: string, defaultValue: string = ''): string => {
    const val = localStorage.getItem(key);
    if (!val) return defaultValue;
    try {
      return JSON.parse(val);
    } catch {
      return val;
    }
  };

  const getFunctionUrl = (functionName: string): string => {
    const isProd = !(
      location.hostname === "localhost" ||
      location.hostname === "builty-2-react-firebase.web.app" ||
      location.hostname === "dev.app.builty.hu"
    );

    const projectId = isProd ? "builty-2-react-firebase-prod" : "builty-2-react-firebase";
    return `https://europe-west1-${projectId}.cloudfunctions.net/${functionName}`;
  };

  const streamChat = async (
    chatMessages: { role: string; content: string }[],
    options: UseOllamaOptions
  ) => {
    const apiKey = getLocalStorageValue('ai_api_key');
    const modelTag = getLocalStorageValue('ai_model_tag');

    if (!apiKey.trim() || !modelTag.trim()) {
      options.onError(OLLAMA_MISSING_CREDENTIALS_ERROR);
      return;
    }

    setIsLoading(true);

    let appCheckToken = '';
    try {
      const tokenResult = await getToken(appCheck, false);
      appCheckToken = tokenResult.token;
    } catch (err) {
      console.error('App Check failed to retrieve token:', err);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let fullText = '';
    try {
      const url = getFunctionUrl('builtyAiProxy');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(appCheckToken ? { 'X-Firebase-AppCheck': appCheckToken } : {}),
        },
        signal: controller.signal,
        body: JSON.stringify({
          apiKey,
          model: modelTag,
          messages: chatMessages,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP hiba! Státusz: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('ReadableStream nem támogatott vagy a válasz üres.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Save the last partial line back to the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const parsed = JSON.parse(line);
            if (parsed.message?.content) {
              const chunk = parsed.message.content;
              fullText += chunk;
              options.onChunk(chunk);
            }
          } catch (e) {
            console.error('Hiba a sor feldolgozása közben:', line, e);
          }
        }
      }

      // Flush any remaining text in the buffer
      if (buffer.trim()) {
        try {
          const parsed = JSON.parse(buffer);
          if (parsed.message?.content) {
            const chunk = parsed.message.content;
            fullText += chunk;
            options.onChunk(chunk);
          }
        } catch (e) {
          console.error('Hiba a puffer végének feldolgozása közben:', buffer, e);
        }
      }

      setIsLoading(false);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      options.onDone(fullText);
    } catch (err: any) {
      setIsLoading(false);
      const wasAborted = controller.signal.aborted || err.name === 'AbortError';
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      if (wasAborted) {
        options.onDone(fullText);
      } else {
        options.onError(err.message || 'Hiba történt a kommunikáció során.');
      }
    }
  };

  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
  };

  return {
    streamChat,
    isLoading,
    stopStreaming,
  };
}
